/**
 * Dữ liệu mẫu và máy chủ giả của các màn Báo cáo doanh nghiệp (module `bao-cao`).
 *
 * Đóng vai backend khi chưa đăng nhập Keycloak thật hoặc khi URL có `?mock=`. Máy chủ giả **từ chối y
 * như backend** — cùng `error_code`, cùng HTTP (bảng mã lỗi ở `modules/bao-cao/README.md`):
 *
 * - ghi vào nguồn `SAP cũ (ngừng đồng bộ)` (đã tắt) nhận `409 BAO_CAO_SOURCE_INACTIVE` ở mọi đường ghi;
 * - tạo nguồn trùng mã (`misa`) nhận `409 BAO_CAO_SOURCE_CODE_EXISTS`;
 * - tắt nguồn mặc định, hoặc đặt nguồn đã tắt làm mặc định, nhận `409 BAO_CAO_SOURCE_DEFAULT_LOCKED`;
 * - tạo thư mục trùng tên trong cùng cha (`Báo cáo tháng`) nhận `409 BAO_CAO_FOLDER_NAME_EXISTS`;
 * - xoá thư mục còn con hay tài liệu (`Báo cáo tháng`) nhận `409 BAO_CAO_FOLDER_NOT_EMPTY`;
 *   `Hồ sơ dự án…` rỗng, xoá được;
 * - chuyển thư mục vào chính nó hoặc con cháu nhận `400 BAO_CAO_FOLDER_CYCLE`;
 * - tải tệp không phải PDF/OOXML nhận `400 BAO_CAO_FILE_TYPE_NOT_SUPPORTED`, trên 50 MB nhận `413`;
 * - phân hệ chưa có số liệu (Dự án, Nhân sự ở nguồn mặc định; mọi phân hệ trừ Tài chính ở Misa) nhận `404`.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây (bảng tông ở `constants.ts`).
 */
import type { ApiError } from '../lib/errors';
import type {
  CreateFolderInput,
  CreateSourceInput,
  DocumentFields,
  DocumentItem,
  DocumentOpen,
  DocumentPage,
  Folder,
  ModuleId,
  ModulePayload,
  QueryDocuments,
  Source,
  TepChon,
  UpdateFolderInput,
  UpdateModulePayload,
  UpdateSourceInput,
} from '../modules/bao-cao-doanh-nghiep/types';

const TOI = '6f1c2b0e-3d4a-4b5c-8d9e-0a1b2c3d4e5f';
const NIL = '00000000-0000-0000-0000-000000000000';
const API_GOC = 'https://api.foxai.vn/api/v1/m/bao-cao';

const loi = (status: number, code: string, message: string, details?: unknown): ApiError => ({ code, status, message, details });
const bayGio = () => new Date().toISOString();

let tuTang = 100;
const idMoi = (tien: string) => `${tien}-${++tuTang}`;

// ─────────────────────────── Nguồn ───────────────────────────

const NGUON: Source[] = [
  { id: 'src-default', code: 'default', name: 'Mặc định', isDefault: true, isManual: true, isActive: true, sortOrder: 0,
    documentsTotal: 0, createdAt: '2026-09-01T02:00:00.000Z', createdBy: NIL, updatedAt: '2026-09-01T02:00:00.000Z' },
  { id: 'src-misa', code: 'misa', name: 'Phần mềm kế toán Misa', description: 'Đồng bộ mỗi đêm từ Misa AMIS.',
    isDefault: false, isManual: false, isActive: true, sortOrder: 1, documentsTotal: 0,
    createdAt: '2026-09-05T02:00:00.000Z', createdBy: TOI, updatedAt: '2026-09-05T02:00:00.000Z' },
  { id: 'src-sap', code: 'sap-cu', name: 'SAP cũ (ngừng đồng bộ)', isDefault: false, isManual: false, isActive: false,
    sortOrder: 2, documentsTotal: 0, createdAt: '2026-08-01T02:00:00.000Z', createdBy: TOI, updatedAt: '2026-10-01T02:00:00.000Z' },
  { id: 'src-cn', code: 'chi-nhanh-mien-trung',
    name: 'Chi nhánh miền Trung — số liệu tổng hợp từ văn phòng Đà Nẵng và Quy Nhơn', isDefault: false, isManual: true,
    isActive: true, sortOrder: 3, documentsTotal: 0, createdAt: '2026-09-20T02:00:00.000Z', createdBy: TOI,
    updatedAt: '2026-09-20T02:00:00.000Z' },
];

/** `?source=` vắng là nguồn mặc định; mã lạ là `404`, như backend. */
function giaiNguon(code: string | undefined): Source {
  const n = code === undefined ? NGUON.find((x) => x.isDefault) : NGUON.find((x) => x.code === code);
  if (!n) throw loi(404, 'NOT_FOUND', `Không có nguồn dữ liệu "${code ?? ''}".`);
  return n;
}

function chanNguonTat(n: Source): void {
  if (!n.isActive) {
    throw loi(409, 'BAO_CAO_SOURCE_INACTIVE', `Nguồn "${n.name}" đã ngừng hoạt động, không ghi thêm được. Bật lại nguồn ở màn Nguồn dữ liệu.`);
  }
}

const nguonTheoId = (id: string): Source => {
  const n = NGUON.find((x) => x.id === id);
  if (!n) throw loi(404, 'NOT_FOUND', 'Không tìm thấy nguồn dữ liệu.');
  return n;
};

// ─────────────────────────── Phân hệ ───────────────────────────

const PHAN_HE = new Map<string, ModulePayload>([
  ['src-default|overview', {
    id: 'overview',
    title: 'Tổng quan',
    documentsTitle: 'Tài liệu mới',
    hero: { label: 'Doanh thu luỹ kế năm', value: '42,8 tỷ', caption: 'So với cùng kỳ năm 2025', delta: '+18,2%', trend: 'up',
      spark: [30.1, 33.4, 36.9, 39.2, 42.8] },
    stats: [
      { id: 'loi-nhuan', label: 'Lợi nhuận gộp', value: '9,6 tỷ', delta: '+6,4%', trend: 'up', accentName: 'success', iconName: 'trending-up' },
      { id: 'cong-no', label: 'Công nợ phải thu', value: '3,1 tỷ', delta: '-4,1%', trend: 'down', accentName: 'warning', iconName: 'receipt' },
      { id: 'nhan-su', label: 'Nhân sự', value: '128 người', trend: 'flat', accentName: 'violet', iconName: 'users' },
    ],
    rank: { title: 'Chi nhánh dẫn đầu', rows: [
      { id: 'dong-1', label: 'Hà Nội', value: '18,2 tỷ' },
      { id: 'dong-2', label: 'TP. Hồ Chí Minh', value: '15,4 tỷ' },
      { id: 'dong-3', label: 'Chi nhánh miền Trung (Đà Nẵng, Quy Nhơn, Nha Trang)', value: '9,2 tỷ' },
    ] },
    updatedAt: '2026-10-20T03:00:00.000Z',
  }],
  ['src-default|finance', {
    id: 'finance',
    title: 'Tài chính',
    documentsTitle: 'Báo cáo tài chính',
    hero: { label: 'Doanh thu quý III', value: '8,42 tỷ', delta: '+12,4%', trend: 'up', spark: [7.1, 7.9, 8.42] },
    stats: [
      { id: 'dong-tien', label: 'Dòng tiền thuần', value: '1,8 tỷ', delta: '+3,0%', trend: 'up', accentName: 'teal', iconName: 'wallet' },
      { id: 'chi-phi', label: 'Chi phí vận hành', value: '2,3 tỷ', delta: '+1,2%', trend: 'up', accentName: 'danger', iconName: 'banknote' },
    ],
    updatedAt: '2026-10-18T03:00:00.000Z',
  }],
  ['src-default|sales', {
    id: 'sales',
    title: 'Kinh doanh',
    stats: [
      { id: 'don-hang', label: 'Đơn hàng trong tháng', value: '1.284', delta: '+9%', trend: 'up', accentName: 'orange', iconName: 'package' },
    ],
    updatedAt: '2026-10-15T03:00:00.000Z',
  }],
  ['src-misa|finance', {
    id: 'finance',
    title: 'Tài chính (Misa)',
    hero: { label: 'Doanh thu quý III', value: '8,40 tỷ', trend: 'up', spark: [7.0, 7.8, 8.4] },
    updatedAt: '2026-10-21T17:00:00.000Z',
  }],
  ['src-sap|finance', {
    id: 'finance',
    title: 'Tài chính (SAP)',
    hero: { label: 'Doanh thu quý II', value: '7,9 tỷ', trend: 'flat', spark: [7.9, 7.9] },
    updatedAt: '2026-06-30T17:00:00.000Z',
  }],
]);

// ─────────────────────────── Thư mục ───────────────────────────

const thuMuc = (id: string, sourceId: string, name: string, parentId: string | null, path: string, sortOrder = 0): Folder => ({
  id, sourceId, name, parentId, path, sortOrder,
  createdAt: '2026-09-10T02:00:00.000Z', createdBy: TOI, updatedAt: '2026-09-10T02:00:00.000Z',
});

const THU_MUC: Folder[] = [
  thuMuc('f-thang', 'src-default', 'Báo cáo tháng', null, 'bao-cao-thang'),
  thuMuc('f-08', 'src-default', '2026-08', 'f-thang', 'bao-cao-thang/2026-08'),
  thuMuc('f-09', 'src-default', '2026-09', 'f-thang', 'bao-cao-thang/2026-09', 1),
  thuMuc('f-quy', 'src-default', 'Báo cáo quý', null, 'bao-cao-quy', 1),
  thuMuc('f-du-an', 'src-default', 'Hồ sơ dự án đầu tư mở rộng nhà máy và kho vận khu vực phía Nam giai đoạn 2026–2028', null,
    'ho-so-du-an-dau-tu-mo-rong', 2),
  thuMuc('f-misa', 'src-misa', 'Sổ sách kế toán', null, 'so-sach-ke-toan'),
];

function slug(ten: string): string {
  return ten.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'thu-muc';
}

function sapThuMuc(): void {
  THU_MUC.sort((a, b) => a.sourceId.localeCompare(b.sourceId) || a.path.localeCompare(b.path));
}

/** Viết lại `path` cả nhánh sau khi đổi tên hoặc chuyển. */
function vietLaiDuongDan(f: Folder): void {
  const cha = f.parentId ? THU_MUC.find((x) => x.id === f.parentId) : undefined;
  f.path = cha ? `${cha.path}/${slug(f.name)}` : slug(f.name);
  for (const con of THU_MUC.filter((x) => x.parentId === f.id)) vietLaiDuongDan(con);
}

const thuMucTheoId = (id: string): Folder => {
  const f = THU_MUC.find((x) => x.id === id);
  if (!f) throw loi(404, 'NOT_FOUND', 'Không tìm thấy thư mục.');
  return f;
};

function chanTrungTen(sourceId: string, parentId: string | null, name: string, boQua?: string): void {
  const trung = THU_MUC.some((x) => x.sourceId === sourceId && x.parentId === parentId && x.id !== boQua
    && x.name.toLowerCase() === name.toLowerCase());
  if (trung) {
    throw loi(409, 'BAO_CAO_FOLDER_NAME_EXISTS', `Đã có thư mục "${name}" ở cùng chỗ. Đặt tên khác.`,
      [{ pointer: '#/name', detail: 'Tên đã có trong thư mục cha này.' }]);
  }
}

function conChau(id: string): Set<string> {
  const ra = new Set<string>([id]);
  let them = true;
  while (them) {
    them = false;
    for (const f of THU_MUC) {
      if (f.parentId && ra.has(f.parentId) && !ra.has(f.id)) {
        ra.add(f.id);
        them = true;
      }
    }
  }
  return ra;
}

// ─────────────────────────── Tài liệu ───────────────────────────

interface DongTaiLieu {
  item: DocumentItem;
  sourceId: string;
  isPublished: boolean;
}

const coTepMau = (id: string, fileName: string, fileSizeBytes: number): Pick<DocumentItem, 'url' | 'fileName' | 'fileSizeBytes'> => ({
  url: `${API_GOC}/documents/${id}/open`,
  fileName,
  fileSizeBytes,
});

const TAI_LIEU: DongTaiLieu[] = [
  { sourceId: 'src-default', isPublished: true, item: { id: 'd-1', title: 'Báo cáo tài chính quý III/2026', type: 'pdf',
    summary: 'Kết quả kinh doanh, dòng tiền và công nợ quý III.', meta: 'PDF · 2,4 MB', iconName: 'banknote', accentName: 'success',
    category: 'finance', folderId: 'f-quy', updatedAt: '2026-10-20T03:00:00.000Z',
    ...coTepMau('d-1', 'bao-cao-tai-chinh-q3-2026.pdf', 2_516_582) } },
  { sourceId: 'src-default', isPublished: true, item: { id: 'd-2', title: 'Báo cáo tháng 9/2026', type: 'pdf',
    iconName: 'file-text', accentName: 'primary', category: 'other', folderId: 'f-09', updatedAt: '2026-10-05T03:00:00.000Z',
    ...coTepMau('d-2', 'bao-cao-thang-09.pdf', 1_048_576) } },
  { sourceId: 'src-default', isPublished: true, item: { id: 'd-3', title: 'Báo cáo tháng 8/2026', type: 'document',
    iconName: 'file-text', accentName: 'primary', category: 'other', folderId: 'f-08', updatedAt: '2026-09-05T03:00:00.000Z',
    ...coTepMau('d-3', 'bao-cao-thang-08.docx', 524_288) } },
  { sourceId: 'src-default', isPublished: true, item: { id: 'd-4', title: 'Bảng giá và chính sách chiết khấu cho đại lý cấp 1 khu vực miền Trung năm 2026',
    url: 'https://files.foxai.vn/bang-gia-2026.pdf', type: 'pdf', summary: 'Áp dụng từ 01/10/2026.', iconName: 'handshake',
    accentName: 'orange', category: 'sales', updatedAt: '2026-10-01T03:00:00.000Z' } },
  { sourceId: 'src-default', isPublished: true, item: { id: 'd-5', title: 'Tiến độ dự án kho Bình Dương', url: 'https://duan.foxai.vn/kho-binh-duong',
    type: 'web', iconName: 'briefcase', accentName: 'violet', category: 'projects', updatedAt: '2026-09-28T03:00:00.000Z' } },
  { sourceId: 'src-default', isPublished: true, item: { id: 'd-6', title: 'Chính sách lương thưởng 2026', type: 'pdf',
    iconName: 'users', accentName: 'sky', category: 'people', updatedAt: '2026-09-15T03:00:00.000Z',
    ...coTepMau('d-6', 'chinh-sach-luong-thuong-2026.pdf', 734_003) } },
  { sourceId: 'src-default', isPublished: true, item: { id: 'd-7', title: 'Biên bản họp giao ban tuần 38', url: 'http://10.0.0.5:8091/bien-ban/tuan-38',
    type: 'unknown', iconName: 'clipboard-list', accentName: 'teal', category: 'other', updatedAt: '2026-09-20T03:00:00.000Z' } },
  { sourceId: 'src-default', isPublished: true, item: { id: 'd-8', title: 'Kế hoạch kinh doanh quý IV', type: 'document',
    iconName: 'target', accentName: 'orange', category: 'sales', updatedAt: '2026-10-10T03:00:00.000Z',
    ...coTepMau('d-8', 'ke-hoach-kinh-doanh-q4.xlsx', 312_000) } },
  { sourceId: 'src-default', isPublished: false, item: { id: 'd-9', title: 'Bản nháp báo cáo quý II (đã gỡ)', url: 'https://files.foxai.vn/q2-nhap.pdf',
    type: 'pdf', iconName: 'file-text', accentName: 'primary', category: 'finance', updatedAt: '2026-07-10T03:00:00.000Z' } },
  { sourceId: 'src-misa', isPublished: true, item: { id: 'd-20', title: 'Sổ cái tháng 9 (Misa)', url: 'https://files.foxai.vn/misa/so-cai-09.pdf',
    type: 'pdf', iconName: 'receipt', accentName: 'success', category: 'finance', folderId: 'f-misa', updatedAt: '2026-10-02T17:00:00.000Z' } },
  { sourceId: 'src-sap', isPublished: true, item: { id: 'd-30', title: 'Báo cáo tồn kho (SAP)', url: 'https://files.foxai.vn/sap/ton-kho.pdf',
    type: 'pdf', iconName: 'package', accentName: 'warning', category: 'other', updatedAt: '2026-06-30T17:00:00.000Z' } },
];

const dongTheoId = (id: string): DongTaiLieu => {
  const d = TAI_LIEU.find((x) => x.item.id === id && x.isPublished);
  if (!d) throw loi(404, 'NOT_FOUND', 'Không tìm thấy tài liệu.');
  return d;
};

/** Bỏ dấu, chữ thường — tìm như `search_norm LIKE` của backend. */
function chuan(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();
}

function kiemTep(tep: TepChon): void {
  if (!/\.(pdf|docx|xlsx|pptx)$/i.test(tep.name)) {
    throw loi(400, 'BAO_CAO_FILE_TYPE_NOT_SUPPORTED', `Tệp "${tep.name}" không phải PDF hay Office (.docx, .xlsx, .pptx).`,
      [{ pointer: '#/file', detail: 'Loại tệp không được hỗ trợ.' }]);
  }
  if (tep.size > 50 * 1024 * 1024) {
    throw loi(413, 'BAO_CAO_FILE_TOO_LARGE', 'Tệp vượt quá 50 MB, vui lòng chia nhỏ hoặc nén tệp rồi tải lên lại.',
      [{ pointer: '#/file', detail: 'Tệp quá lớn.' }]);
  }
}

function kiemThuMucCuaNguon(folderId: string | null | undefined, sourceId: string): void {
  if (folderId === null || folderId === undefined) return;
  const f = THU_MUC.find((x) => x.id === folderId);
  if (!f || f.sourceId !== sourceId) {
    throw loi(400, 'INVALID_ARGUMENT', 'Thư mục không thuộc nguồn này.', [{ pointer: '#/folderId', detail: 'Thư mục không tồn tại.' }]);
  }
}

function dungTaiLieu(id: string, du: DocumentFields, tep: TepChon | null, cu?: DocumentItem): DocumentItem {
  const moi: DocumentItem = {
    ...(cu ?? { id, title: '', url: '', type: 'unknown', iconName: 'file-text', accentName: 'primary', category: 'other', updatedAt: bayGio() }),
  };
  if (du.title !== undefined) moi.title = du.title;
  if (du.category !== undefined) moi.category = du.category;
  if (du.iconName !== undefined) moi.iconName = du.iconName;
  if (du.accentName !== undefined) moi.accentName = du.accentName;
  if (du.folderId !== undefined) {
    if (du.folderId === null) delete moi.folderId;
    else moi.folderId = du.folderId;
  }
  for (const k of ['summary', 'meta', 'thumbnail'] as const) {
    const v = du[k];
    if (v === undefined) continue;
    if (v === '') delete moi[k];
    else moi[k] = v;
  }
  if (tep) {
    Object.assign(moi, coTepMau(id, tep.name, tep.size));
    moi.type = /\.pdf$/i.test(tep.name) ? 'pdf' : 'document';
  } else if (du.url !== undefined) {
    // Có `url` là bỏ tệp đang gắn.
    moi.url = du.url;
    delete moi.fileName;
    delete moi.fileSizeBytes;
    if (du.type !== undefined) moi.type = du.type;
  }
  moi.updatedAt = du.updatedAt ?? bayGio();
  return moi;
}

function demTaiLieu(sourceId: string, folderId?: string | null): number {
  return TAI_LIEU.filter((d) => d.isPublished && d.sourceId === sourceId
    && (folderId === undefined || (d.item.folderId ?? null) === folderId)).length;
}

// ─────────────────────────── Máy chủ ───────────────────────────

const chuaNhap = (): ApiError => loi(404, 'NOT_FOUND', 'Phân hệ chưa có số liệu.');

export const mayChuBaoCao = {
  /** Lỗi `404` của phân hệ chưa nhập số liệu — repository dùng cho `?mock=empty`. */
  chuaNhap,

  phanHe(id: ModuleId, source: string | undefined): ModulePayload {
    const n = giaiNguon(source);
    const p = PHAN_HE.get(`${n.id}|${id}`);
    if (!p) throw chuaNhap();
    const docs = TAI_LIEU.filter((d) => d.isPublished && d.sourceId === n.id && d.item.category === id);
    return { ...structuredClone(p), documentsTotal: docs.length, documents: docs.slice(0, 5).map((d) => ({ ...d.item })) };
  },

  luuPhanHe(id: ModuleId, than: UpdateModulePayload, source: string | undefined): ModulePayload {
    const n = giaiNguon(source);
    chanNguonTat(n);
    if (!than.title?.trim()) {
      throw loi(400, 'INVALID_ARGUMENT', 'Thiếu tiêu đề phân hệ.', [{ pointer: '#/title', detail: 'Bắt buộc.' }]);
    }
    if (than.hero && than.hero.spark.length < 2) {
      throw loi(400, 'INVALID_ARGUMENT', 'Đường xu hướng cần ít nhất 2 điểm.', [{ pointer: '#/hero/spark', detail: 'Cần ít nhất 2 điểm.' }]);
    }
    // Ghi đè trọn gói: khối vắng là xoá khối.
    const moi: ModulePayload = {
      id,
      title: than.title,
      ...(than.documentsTitle ? { documentsTitle: than.documentsTitle } : {}),
      ...(than.hero ? { hero: than.hero } : {}),
      ...(than.stats && than.stats.length > 0 ? { stats: than.stats } : {}),
      ...(than.rank ? { rank: than.rank } : {}),
      updatedAt: bayGio(),
    };
    PHAN_HE.set(`${n.id}|${id}`, structuredClone(moi));
    return mayChuBaoCao.phanHe(id, source);
  },

  taiLieu(q: QueryDocuments): DocumentPage {
    if (q.folder && q.chuaPhanLoai) throw loi(400, 'INVALID_ARGUMENT', 'Không dùng cùng lúc "folder" và "chuaPhanLoai".');
    const n = giaiNguon(q.source);
    const trongNhanh = q.folder ? (q.deep ? conChau(q.folder) : new Set([q.folder])) : undefined;
    const tu = q.q ? chuan(q.q).split(/\s+/).filter(Boolean).slice(0, 8) : [];
    const ds = TAI_LIEU.filter((d) => d.isPublished && d.sourceId === n.id)
      .map((d) => d.item)
      .filter((d) => (!q.category || d.category === q.category)
        && (!trongNhanh || (d.folderId !== undefined && trongNhanh.has(d.folderId)))
        && (!q.chuaPhanLoai || d.folderId === undefined)
        && tu.every((w) => chuan(`${d.title} ${d.summary ?? ''}`).includes(w)))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || b.id.localeCompare(a.id));
    const batDau = q.cursor ? Number(q.cursor) : 0;
    const trang = ds.slice(batDau, batDau + q.limit).map((d) => ({ ...d }));
    const tiep = batDau + q.limit < ds.length ? String(batDau + q.limit) : null;
    return { items: trang, total: ds.length, nextCursor: tiep };
  },

  moTaiLieu(id: string): DocumentOpen {
    const d = dongTheoId(id).item;
    if (d.fileName) {
      // Bản giả không có object storage: trả một tệp chữ thay cho URL ký 300 giây.
      return { url: `data:text/plain;charset=utf-8,${encodeURIComponent(`Tệp mẫu: ${d.fileName}`)}`, type: d.type };
    }
    return { url: d.url, type: d.type };
  },

  taoTaiLieu(du: DocumentFields, tep: TepChon | null, source: string | undefined): DocumentItem {
    const n = giaiNguon(source);
    chanNguonTat(n);
    if (tep) kiemTep(tep);
    else if (!du.url) throw loi(400, 'INVALID_ARGUMENT', 'Cần liên kết hoặc tệp.', [{ pointer: '#/url', detail: 'Bắt buộc.' }]);
    kiemThuMucCuaNguon(du.folderId, n.id);
    const id = idMoi('d');
    const item = dungTaiLieu(id, du, tep);
    TAI_LIEU.push({ item, sourceId: n.id, isPublished: du.isPublished ?? true });
    return { ...item };
  },

  suaTaiLieu(id: string, du: DocumentFields, tep: TepChon | null): DocumentItem {
    const d = dongTheoId(id);
    chanNguonTat(nguonTheoId(d.sourceId));
    if (tep) kiemTep(tep);
    kiemThuMucCuaNguon(du.folderId, d.sourceId);
    d.item = dungTaiLieu(id, du, tep, d.item);
    if (du.isPublished !== undefined) d.isPublished = du.isPublished;
    return { ...d.item };
  },

  goTaiLieu(id: string): { id: string; isPublished: boolean } {
    const d = dongTheoId(id);
    chanNguonTat(nguonTheoId(d.sourceId));
    d.isPublished = false;
    return { id, isPublished: false };
  },

  thuMuc(source: string | undefined, kemSoLuong: boolean): Folder[] {
    const n = giaiNguon(source);
    return THU_MUC.filter((f) => f.sourceId === n.id).map((f) => ({
      ...f,
      ...(kemSoLuong ? { documentsTotal: demTaiLieu(n.id, f.id) } : {}),
    }));
  },

  demChuaPhanLoai(source: string | undefined): { documentsTotal: number } {
    return { documentsTotal: demTaiLieu(giaiNguon(source).id, null) };
  },

  taoThuMuc(du: CreateFolderInput): Folder {
    const n = giaiNguon(du.source);
    chanNguonTat(n);
    const ten = du.name.trim();
    if (!ten) throw loi(400, 'INVALID_ARGUMENT', 'Tên thư mục không được trống.', [{ pointer: '#/name', detail: 'Bắt buộc.' }]);
    const parentId = du.parentId ?? null;
    if (parentId !== null && thuMucTheoId(parentId).sourceId !== n.id) {
      throw loi(400, 'INVALID_ARGUMENT', 'Thư mục cha không thuộc nguồn này.', [{ pointer: '#/parentId', detail: 'Sai nguồn.' }]);
    }
    chanTrungTen(n.id, parentId, ten);
    const f = thuMuc(idMoi('f'), n.id, ten, parentId, '', THU_MUC.filter((x) => x.parentId === parentId).length);
    f.createdAt = bayGio();
    f.updatedAt = f.createdAt;
    THU_MUC.push(f);
    vietLaiDuongDan(f);
    sapThuMuc();
    return { ...f };
  },

  suaThuMuc(id: string, du: UpdateFolderInput): Folder {
    const f = thuMucTheoId(id);
    chanNguonTat(nguonTheoId(f.sourceId));
    if (du.name !== undefined) {
      const ten = du.name.trim();
      if (!ten) throw loi(400, 'INVALID_ARGUMENT', 'Tên thư mục không được trống.', [{ pointer: '#/name', detail: 'Bắt buộc.' }]);
      chanTrungTen(f.sourceId, f.parentId, ten, f.id);
      f.name = ten;
      vietLaiDuongDan(f);
    }
    if (du.sortOrder !== undefined) f.sortOrder = du.sortOrder;
    f.updatedAt = bayGio();
    sapThuMuc();
    return { ...f };
  },

  chuyenThuMuc(id: string, parentId: string | null): Folder {
    const f = thuMucTheoId(id);
    chanNguonTat(nguonTheoId(f.sourceId));
    if (parentId !== null) {
      if (conChau(id).has(parentId)) {
        throw loi(400, 'BAO_CAO_FOLDER_CYCLE', 'Không chuyển được thư mục vào chính nó hoặc thư mục con của nó.');
      }
      if (thuMucTheoId(parentId).sourceId !== f.sourceId) {
        throw loi(400, 'INVALID_ARGUMENT', 'Thư mục cha không thuộc nguồn này.', [{ pointer: '#/parentId', detail: 'Sai nguồn.' }]);
      }
    }
    chanTrungTen(f.sourceId, parentId, f.name, f.id);
    f.parentId = parentId;
    vietLaiDuongDan(f);
    f.updatedAt = bayGio();
    sapThuMuc();
    return { ...f };
  },

  xoaThuMuc(id: string): { id: string } {
    const f = thuMucTheoId(id);
    chanNguonTat(nguonTheoId(f.sourceId));
    const conThuMuc = THU_MUC.some((x) => x.parentId === id);
    const conTaiLieu = TAI_LIEU.some((d) => d.isPublished && d.item.folderId === id);
    if (conThuMuc || conTaiLieu) {
      throw loi(409, 'BAO_CAO_FOLDER_NOT_EMPTY', `Thư mục "${f.name}" còn thư mục con hoặc tài liệu. Chuyển chúng đi trước khi xoá.`);
    }
    THU_MUC.splice(THU_MUC.indexOf(f), 1);
    return { id };
  },

  nguon(): Source[] {
    return NGUON.slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((n) => ({ ...n, documentsTotal: demTaiLieu(n.id) }));
  },

  taoNguon(du: CreateSourceInput): Source {
    if (!/^[a-z0-9][a-z0-9-]{0,39}$/.test(du.code)) {
      throw loi(400, 'INVALID_ARGUMENT', 'Mã nguồn chỉ gồm chữ thường, số và gạch ngang.', [{ pointer: '#/code', detail: 'Sai định dạng.' }]);
    }
    if (NGUON.some((n) => n.code === du.code)) {
      throw loi(409, 'BAO_CAO_SOURCE_CODE_EXISTS', `Mã nguồn "${du.code}" đã có. Đặt tên khác để sinh mã khác.`,
        [{ pointer: '#/code', detail: 'Mã đã có.' }]);
    }
    const n: Source = {
      id: idMoi('src'), code: du.code, name: du.name.trim(), ...(du.description ? { description: du.description } : {}),
      isDefault: false, isManual: du.isManual ?? true, isActive: true, sortOrder: NGUON.length, documentsTotal: 0,
      createdAt: bayGio(), createdBy: TOI, updatedAt: bayGio(),
    };
    NGUON.push(n);
    return { ...n };
  },

  suaNguon(id: string, du: UpdateSourceInput): Source {
    const n = nguonTheoId(id);
    if (du.name !== undefined) n.name = du.name.trim();
    if (du.description !== undefined) {
      if (du.description.trim() === '') delete n.description;
      else n.description = du.description.trim();
    }
    if (du.sortOrder !== undefined) n.sortOrder = du.sortOrder;
    n.updatedAt = bayGio();
    return { ...n, documentsTotal: demTaiLieu(n.id) };
  },

  datNguonMacDinh(id: string): Source {
    const n = nguonTheoId(id);
    if (!n.isActive) {
      throw loi(409, 'BAO_CAO_SOURCE_DEFAULT_LOCKED', `Nguồn "${n.name}" đã ngừng hoạt động, không đặt làm mặc định được. Bật lại trước.`);
    }
    for (const x of NGUON) x.isDefault = x.id === id;
    n.updatedAt = bayGio();
    return { ...n, documentsTotal: demTaiLieu(n.id) };
  },

  datTrangThaiNguon(id: string, bat: boolean): Source {
    const n = nguonTheoId(id);
    if (!bat && n.isDefault) {
      throw loi(409, 'BAO_CAO_SOURCE_DEFAULT_LOCKED', 'Không ngừng được nguồn mặc định. Đặt nguồn khác làm mặc định trước.');
    }
    n.isActive = bat;
    n.updatedAt = bayGio();
    return { ...n, documentsTotal: demTaiLieu(n.id) };
  },
};
